import { useRef, useState, ChangeEvent, ChangeEventHandler, DragEvent } from 'react'
import { CircularProgress } from '@mui/material'

import * as S from './styles'
import { Icon } from 'components'
import { theme } from 'styles/theme/hav-theme'

type OnChangeUpload = ChangeEventHandler<HTMLInputElement>

export interface UploadAreaProps {
  id?: string
  name?: string
  accept?: string
  disabled?: boolean
  required?: boolean
  loading?: boolean
  loadingProgress?: number
  uploadAreaText?: string | JSX.Element
  onChange: OnChangeUpload
}

export const UploadArea: React.FC<UploadAreaProps> = ({
  name = undefined,
  id,
  accept = '',
  onChange,
  required = false,
  disabled = false,
  loading = false,
  loadingProgress,
  uploadAreaText
}) => {
  const InputRef = useRef<HTMLInputElement>(null)
  const [dragActive, setDragActive] = useState(false)

  const handleDrag = function (e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = function (e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      if (InputRef.current) {
        const changeEvent = new Event('change', { bubbles: true })
        InputRef.current.files = e.dataTransfer.files
        InputRef.current.dispatchEvent(changeEvent)
      }
    }
  }

  const handleOnchange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e) {
      onChange(e)
    }
  }

  return (
    <S.UploadArea
      isDragActive={dragActive}
      onDragEnter={handleDrag}
      loading={loading ? 'true' : 'false'}
    >
      <input
        ref={InputRef}
        id={id}
        type="file"
        accept={accept}
        name={name}
        required={required}
        disabled={disabled}
        onChange={handleOnchange}
      />
      <label id={id} htmlFor={id}>
        <S.UploadAreaContent>
          <S.IconWrapper>
            <Icon icon="empty" color={theme.colors.neutral.high} size="44" />
          </S.IconWrapper>
          <S.TextContainer>
            <S.Title>O pedido está vazio</S.Title>
            {uploadAreaText && <S.Text disabled={disabled}>{uploadAreaText}</S.Text>}
          </S.TextContainer>
        </S.UploadAreaContent>
      </label>
      {dragActive && (
        <S.DragOverlay
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <S.OverlayContent>
            <S.IconWrapper>
              <Icon icon="upload" size="42" />
            </S.IconWrapper>
            <S.Title>Solte o arquivo aqui</S.Title>
          </S.OverlayContent>
        </S.DragOverlay>
      )}
      {loading && (
        <S.LoadingOverlay>
          <S.LoadingAreaContent>
            <S.IconWrapper>
              <CircularProgress />
            </S.IconWrapper>
            <S.TextContainer>
              <S.Title>Carregando...</S.Title>
              {!!loadingProgress && (
                <S.LoadingBar>
                  <S.LoadingFill progress={loadingProgress} />
                </S.LoadingBar>
              )}
            </S.TextContainer>
          </S.LoadingAreaContent>
        </S.LoadingOverlay>
      )}
    </S.UploadArea>
  )
}
